/**
 * Agreement React Query Hooks
 * 
 * Provides React Query hooks for agreement/membership operations with caching,
 * optimistic updates, and automatic refetching.
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { agreementService, type CreateAgreementInput, type UpdateAgreementInput } from '../services';
import type { Agreement } from '../repositories/AgreementRepository';

/**
 * Query keys for agreement-related queries
 */
export const agreementKeys = {
  all: ['agreements'] as const,
  lists: () => [...agreementKeys.all, 'list'] as const,
  list: (filters?: any) => [...agreementKeys.lists(), filters] as const,
  details: () => [...agreementKeys.all, 'detail'] as const,
  detail: (id: string) => [...agreementKeys.details(), id] as const,
  customer: (customerId: string) => [...agreementKeys.lists(), 'customer', customerId] as const,
  expiring: (days: number) => [...agreementKeys.lists(), 'expiring', days] as const,
  statistics: () => [...agreementKeys.all, 'statistics'] as const,
};

/**
 * Hook to fetch all agreements
 */
export function useAgreements(options?: UseQueryOptions<Agreement[], Error>) {
  return useQuery<Agreement[], Error>({
    queryKey: agreementKeys.lists(),
    queryFn: () => agreementService.getAgreements(),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook to fetch a single agreement by ID
 */
export function useAgreement(
  id: string,
  options?: UseQueryOptions<Agreement | null, Error>
) {
  return useQuery<Agreement | null, Error>({
    queryKey: agreementKeys.detail(id),
    queryFn: () => agreementService.getAgreement(id),
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
    ...options,
  });
}

/**
 * Hook to fetch customer agreements
 */
export function useCustomerAgreements(
  customerId: string | undefined,
  options?: UseQueryOptions<Agreement[], Error>
) {
  return useQuery<Agreement[], Error>({
    queryKey: agreementKeys.customer(customerId || ''),
    queryFn: () => {
      if (!customerId) {
        throw new Error('Customer ID is required');
      }
      return agreementService.getCustomerAgreements(customerId);
    },
    staleTime: 3 * 60 * 1000,
    enabled: !!customerId,
    ...options,
  });
}

/**
 * Hook to fetch expiring agreements
 */
export function useExpiringAgreements(
  days: number = 30,
  options?: UseQueryOptions<Agreement[], Error>
) {
  return useQuery<Agreement[], Error>({
    queryKey: agreementKeys.expiring(days),
    queryFn: () => agreementService.getExpiringAgreements(days),
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

/**
 * Hook to create a new agreement
 */
export function useCreateAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateAgreementInput) => agreementService.createAgreement(input),
    onSuccess: (newAgreement) => {
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agreementKeys.statistics() });
      
      if (newAgreement.customer_id) {
        queryClient.invalidateQueries({ 
          queryKey: agreementKeys.customer(newAgreement.customer_id) 
        });
      }
      
      queryClient.setQueryData(agreementKeys.detail(newAgreement.id), newAgreement);
    },
  });
}

/**
 * Hook to update an agreement
 */
export function useUpdateAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAgreementInput }) =>
      agreementService.updateAgreement(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: agreementKeys.detail(id) });
      
      const previousAgreement = queryClient.getQueryData<Agreement>(agreementKeys.detail(id));
      
      if (previousAgreement) {
        queryClient.setQueryData<Agreement>(agreementKeys.detail(id), {
          ...previousAgreement,
          ...data,
        });
      }
      
      return { previousAgreement };
    },
    onError: (err, { id }, context) => {
      if (context?.previousAgreement) {
        queryClient.setQueryData(agreementKeys.detail(id), context.previousAgreement);
      }
    },
    onSuccess: (updatedAgreement, { id }) => {
      queryClient.setQueryData(agreementKeys.detail(id), updatedAgreement);
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() });
      
      if (updatedAgreement.customer_id) {
        queryClient.invalidateQueries({ 
          queryKey: agreementKeys.customer(updatedAgreement.customer_id) 
        });
      }
    },
  });
}

/**
 * Hook to cancel an agreement
 */
export function useCancelAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, cancelledBy, reason }: { id: string; cancelledBy: string; reason?: string }) =>
      agreementService.cancelAgreement(id, cancelledBy, reason || ''),
    onSuccess: (cancelledAgreement) => {
      queryClient.setQueryData(agreementKeys.detail(cancelledAgreement.id), cancelledAgreement);
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agreementKeys.statistics() });
      
      const customerId = cancelledAgreement.customer_id;
      if (typeof customerId === 'string') {
        queryClient.invalidateQueries({ 
          queryKey: agreementKeys.customer(customerId) 
        });
      }
    },
  });
}

/**
 * Hook to renew an agreement
 */
export function useRenewAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: any }) =>
      agreementService.renewAgreement(id, input),
    onSuccess: (renewedAgreement) => {
      queryClient.setQueryData(agreementKeys.detail(renewedAgreement.id), renewedAgreement);
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agreementKeys.expiring(30) });
      queryClient.invalidateQueries({ queryKey: agreementKeys.statistics() });
      
      if (renewedAgreement.customer_id) {
        queryClient.invalidateQueries({ 
          queryKey: agreementKeys.customer(renewedAgreement.customer_id) 
        });
      }
    },
  });
}

/**
 * Hook to suspend an agreement
 */
export function useSuspendAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, suspendedBy, reason }: { id: string; suspendedBy: string; reason?: string }) =>
      agreementService.suspendAgreement(id, suspendedBy, reason || ''),
    onSuccess: (suspendedAgreement) => {
      queryClient.setQueryData(agreementKeys.detail(suspendedAgreement.id), suspendedAgreement);
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() });
      
      const customerId = suspendedAgreement.customer_id;
      if (typeof customerId === 'string') {
        queryClient.invalidateQueries({ 
          queryKey: agreementKeys.customer(customerId) 
        });
      }
    },
  });
}

/**
 * Hook to reactivate an agreement
 */
export function useReactivateAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reactivatedBy }: { id: string; reactivatedBy: string }) => 
      agreementService.reactivateAgreement(id, reactivatedBy),
    onSuccess: (reactivatedAgreement) => {
      queryClient.setQueryData(agreementKeys.detail(reactivatedAgreement.id), reactivatedAgreement);
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agreementKeys.statistics() });
      
      const customerId = reactivatedAgreement.customer_id;
      if (typeof customerId === 'string') {
        queryClient.invalidateQueries({ 
          queryKey: agreementKeys.customer(customerId) 
        });
      }
    },
  });
}
